import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type { DetailedChangeset } from 'ember-changeset/types';
import { dropTask, restartableTask } from 'ember-concurrency';
import type PrinterModel from 'garaje/models/printer';
import type ExporterService from 'garaje/services/exporter';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { getPrinterReachability, type PrinterReachability } from 'garaje/utils/printer-reachability';

import type { VisitorsDevicesPrintersIndexRouteModel } from './route';

interface PrinterWithReachability {
  printer: PrinterModel;
  reachability: PrinterReachability | undefined;
}

export default class VisitorsDevicesPrintersIndexController extends Controller {
  declare model: VisitorsDevicesPrintersIndexRouteModel;

  @service declare abilities: AbilitiesService;
  @service declare exporter: ExporterService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;
  @service declare metrics: MetricsService;

  get printersWithReachability(): PrinterWithReachability[] {
    const printers = this.model.printers.slice();
    const failoverEnabled = this.featureFlags.isEnabled('visitors-printer-failover');
    return printers.map((printer) => ({
      printer,
      reachability: failoverEnabled ? getPrinterReachability(printer, printers) : undefined,
    }));
  }

  @tracked editPrinterId = null;

  get hasBluetoothPrinters(): boolean {
    return this.model.canAccessMultiplePrinters && this.model.printers.slice()[0]?.connectionType === 'bluetooth';
  }

  get canAddAnotherPrinter(): boolean {
    const addingFirstPrinter = this.model.printers.length === 0;
    const canAddPrinters = this.abilities.can('create printers');

    return Boolean(canAddPrinters && (addingFirstPrinter || this.model.canAccessMultiplePrinters));
  }

  savePrinterTask = restartableTask(async (printerChangeset: DetailedChangeset<PrinterModel>) => {
    try {
      await printerChangeset.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      this.editPrinterId = null;
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  deletePrinterTask = dropTask(async (printer: PrinterModel) => {
    try {
      await printer.destroyRecord();
      this.flashMessages.showAndHideFlash('success', 'Printer removed successfully');
      if (this.model.printers.length === 0) {
        // if last printer was just removed...
        if (this.canAddAnotherPrinter && this.model.vrSubscription?.canEnableBadgePrinting) {
          // ...and it's possible for the current user to add new printers,
          // redirect to the "new printer" page.
          // (This is the same logic used to determine whether to show the "new printer" button on the page.)
          void this.router.transitionTo('visitors.devices.printers.new');
        }
      }
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  exportPrintersTask = dropTask(async () => {
    try {
      // Load all locations for printers before exporting (needed for CSV)
      await Promise.all(this.model.printers.map((printer) => printer.location));

      // @ts-expect-error - exportCsv is an ember-concurrency task with perform method
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.exporter.exportCsv.perform('printers.csv', this.model.printers);
      this.flashMessages.showAndHideFlash('success', 'Printers exported successfully');
    } catch (e: unknown) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  @action
  cancelChanges(printerChangeset: DetailedChangeset<PrinterModel>): void {
    printerChangeset.rollback();
    this.editPrinterId = null;
  }

  @action
  trackOverviewClick(): void {
    this.metrics.trackEvent('Printer Overview Clicked', {
      // RecordArray.length can be ComputedProperty; assertion needed for analytics types
      printer_count: this.model.printers.length as number,
      offline_printers_count: this.model.printers.filter((p) => !p.isOperational).length,
      has_bluetooth_printers: this.hasBluetoothPrinters,
      can_enable_badge_printing: this.model.vrSubscription?.canEnableBadgePrinting,
    });
  }
}
